import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { createRegistration } from "@/lib/api";
import { CheckCircle2, Loader2, Mail, CreditCard } from "lucide-react";

const TICKETS = [
  { id: "delegate", name: "Delegate", perks: "Networking · Gala Dinner" },
  { id: "speaker", name: "Keynote Speaker", perks: "Are you interested in a Speaking Slot · Panel Discussion · Fireside Chat?" },
  { id: "sponsor", name: "Sponsor", perks: "Booth · Brand placement · Dedicated sessions" },
  { id: "student", name: "Tech Leaders — Founder(s) & CXO", perks: "Feature & Felicitation as Transformational Tech Leaders" },
];

const PASS_OPTIONS = {
  delegate: [
    { value: "1 Pass", label: "1 Pass" },
    { value: "2 Passes", label: "2 Passes" },
    { value: "3 Passes", label: "3 Passes" },
    { value: "4 Passes", label: "4 Passes" },
    { value: "5 Passes", label: "5 Passes" },
  ],
};

const SUB_TIERS = {
  sponsor: [
    { value: "Gold", label: "Gold" },
    { value: "Platinum", label: "Platinum" },
    { value: "Diamond", label: "Diamond" },
  ],
  student: [
    { value: "In Person - Leader Participation", label: "In Person - Leader Participation" },
    { value: "Remote - Leader Participation", label: "Remote - Leader Participation" },
  ],
};

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  organization: "",
  role: "",
  ticket_type: "delegate",
  pass_option: "",
  sub_tier: "",
};

export const Registration = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (submitting) return;
    if (!form.full_name || !form.email || !form.phone || !form.organization || !form.role) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (PASS_OPTIONS[form.ticket_type] && !form.pass_option) {
      toast.error("Please select a pass option.");
      return;
    }
    if (SUB_TIERS[form.ticket_type] && !form.sub_tier) {
      toast.error("Please select a tier.");
      return;
    }

    setSubmitting(true);
    try {
      await createRegistration({
        ...form,
        intent: "enquiry",
        amount_inr: 0,
      });
      toast.success("Enquiry received. We'll reach out within 48 hrs.");
      setDone(true);
      setForm(INITIAL);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="registration" data-testid="registration-section" className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-16">
          {/* Left: Persuasion */}
          <div className="col-span-12 lg:col-span-5">
            <div className="overline text-[#FF2A00] mb-4">/ 07 — Registration</div>
            <div className="flex flex-wrap sm:flex-nowrap items-end gap-x-5 gap-y-3">
              <h2 className="shrink-0 font-display text-4xl md:text-5xl uppercase tracking-tighter">
                Claim your
                <br />
                <span className="text-[#002FA7]">seat.</span>
              </h2>
              <div className="max-w-[12rem] pb-1">
                <p
                  data-testid="reg-global-london-notice"
                  className="animate-bot-blink font-display text-sm text-[#002FA7] leading-snug"
                >
                  Registrations open for BoT Global, London March 2027.
                </p>
              </div>
            </div>
            <p className="mt-6 text-zinc-700">
              Seats are capped at 500 to preserve the quality of the floor.
            </p>

            <div className="mt-10 space-y-4">
              {TICKETS.map((t) => (
                <div key={t.id} className="relative">
                  <button
                    type="button"
                    data-testid={`ticket-${t.id}`}
                    onClick={() => setForm({ ...form, ticket_type: t.id, sub_tier: "", pass_option: "" })}
                    className={`w-full text-left p-5 border transition-colors ${
                      form.ticket_type === t.id
                        ? "border-black bg-[#002FA7] text-white"
                        : "border-zinc-200 bg-white hover:border-black"
                    }`}
                  >
                    <div className="font-display text-lg uppercase tracking-tight">
                      {t.name}
                    </div>
                    <div className={`text-xs mt-2 ${t.id === "sponsor" || t.id === "student" ? "pr-28" : ""} ${form.ticket_type === t.id ? "text-white/80" : "text-zinc-500"}`}>
                      {t.perks}
                    </div>
                  </button>
                  {t.id === "sponsor" && (
                    <a
                      href="/sponsorship"
                      data-testid="sponsor-details-link"
                      className={`absolute bottom-4 right-5 overline text-[10px] font-bold tracking-[0.2em] underline underline-offset-4 ${
                        form.ticket_type === t.id
                          ? "text-white hover:text-[#FF2A00]"
                          : "text-[#002FA7] hover:text-[#FF2A00]"
                      }`}
                    >
                      View tiers →
                    </a>
                  )}
                  {t.id === "student" && (
                    <a
                      href="/tech-leaders"
                      data-testid="tech-leaders-details-link"
                      className={`absolute bottom-4 right-5 overline text-[10px] font-bold tracking-[0.2em] underline underline-offset-4 ${
                        form.ticket_type === t.id
                          ? "text-white hover:text-[#FF2A00]"
                          : "text-[#002FA7] hover:text-[#FF2A00]"
                      }`}
                    >
                      View tiers →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="col-span-12 lg:col-span-7">
            <div className="border border-zinc-200 p-6 md:p-10 bg-zinc-50 relative">
              <div className="absolute top-0 right-0 bg-[#FF2A00] text-white px-3 py-1 overline text-[10px]">
                / Enquiry Form
              </div>

              {done ? (
                <div data-testid="registration-success" className="py-10 text-center">
                  <CheckCircle2 size={48} className="mx-auto text-[#002FA7]" />
                  <h3 className="font-display text-2xl uppercase tracking-tight mt-6">
                    You&apos;re in.
                  </h3>
                  <p className="text-zinc-600 mt-3 max-w-md mx-auto">
                    A confirmation has been queued, our team will connect with you within
                    the next 48 hrs.
                  </p>
                  <button
                    data-testid="registration-reset"
                    onClick={() => setDone(false)}
                    className="mt-8 inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-xs font-bold tracking-[0.2em] uppercase hover:bg-[#002FA7] transition-colors"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); submit(); }}
                  data-testid="registration-form"
                  className="grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                  <Field
                    label="Full Name"
                    testid="reg-full_name"
                    required
                    value={form.full_name}
                    onChange={onChange("full_name")}
                    placeholder="Ada Lovelace"
                  />
                  <Field
                    label="Work Email"
                    testid="reg-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="ada@example.com"
                  />
                  <Field
                    label="Phone"
                    testid="reg-phone"
                    required
                    value={form.phone}
                    onChange={onChange("phone")}
                    placeholder="+91 98xxxxxxx"
                  />
                  <Field
                    label="Organization"
                    testid="reg-organization"
                    required
                    value={form.organization}
                    onChange={onChange("organization")}
                    placeholder="Acme Corp"
                  />
                  <Field
                    label="Role / Title"
                    testid="reg-role"
                    required
                    value={form.role}
                    onChange={onChange("role")}
                    placeholder="CIO"
                    className="md:col-span-2"
                  />

                  {form.ticket_type !== "student" && (
                  <div className="md:col-span-2">
                    <label className="overline text-zinc-500 mb-2 block">
                      {form.ticket_type === "delegate" ? "Number of Passes" : "Selected Pass"}
                    </label>
                    {PASS_OPTIONS[form.ticket_type] ? (
                      <select
                        data-testid="reg-pass-option"
                        required
                        value={form.pass_option}
                        onChange={onChange("pass_option")}
                        className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors appearance-none cursor-pointer"
                      >
                        <option value="" disabled>
                          {form.ticket_type === "delegate" ? "Select number of passes…" : "Select a pass…"}
                        </option>
                        {PASS_OPTIONS[form.ticket_type].map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="w-full bg-white border border-zinc-300 px-4 py-3 font-mono text-sm">
                        <span data-testid="selected-ticket-label">
                          {TICKETS.find((t) => t.id === form.ticket_type)?.name}
                        </span>
                      </div>
                    )}
                  </div>
                  )}

                  {SUB_TIERS[form.ticket_type] && (
                    <div className="md:col-span-2">
                      <label className="overline text-zinc-500 mb-2 block">
                        {form.ticket_type === "sponsor" ? "Sponsorship Tier" : "Recognition Tier"}
                      </label>
                      <select
                        data-testid="reg-sub-tier"
                        required
                        value={form.sub_tier}
                        onChange={onChange("sub_tier")}
                        className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors appearance-none cursor-pointer"
                      >
                        <option value="" disabled>
                          Select a tier…
                        </option>
                        {SUB_TIERS[form.ticket_type].map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="md:col-span-2 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="registration-send-enquiry"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending
                        </>
                      ) : (
                        <>
                          <Mail size={16} /> Send Enquiry
                        </>
                      )}
                    </button>
                  </div>

                  <p className="md:col-span-2 text-[11px] text-zinc-500 mt-2">
                    By submitting you agree to the BoT Summit Code of Conduct and consent
                    to occasional updates about the program. Our team will share the
                    participation package and payment details within 48 hrs.
                  </p>

                  <div className="md:col-span-2 mt-2 border-t border-zinc-200 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="overline text-zinc-500 text-[11px]">
                      Prefer to pay online now?
                    </div>
                    <Link
                      to="/international-pay"
                      data-testid="registration-pay-online-link"
                      className="inline-flex items-center gap-2 border-2 border-[#002FA7] text-[#002FA7] px-6 py-3 text-xs font-bold tracking-[0.25em] uppercase hover:bg-[#002FA7] hover:text-white transition-colors"
                    >
                      <CreditCard size={14} /> Pay Online →
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, className = "", ...rest }) => (
  <div className={className}>
    <label className="overline text-zinc-500 mb-2 block">{label}</label>
    <input
      data-testid={testid}
      className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
      {...rest}
    />
  </div>
);

export default Registration;
